import React, { useState, useEffect, useRef } from 'react';

interface GameStat {
    value: number;
    label?: string;
    updatedAt: number;
    lastUpdateMetadata?: string;
}

interface GameStatsOverlayProps {
    stats: Record<string, GameStat>;
    containerClassName?: string;
    scale?: number;
}

const THEMES: Record<string, { color: string, shadow: string }> = {
    wins: { color: '#fbbf24', shadow: 'rgba(251, 191, 36, 0.4)' }, // Amber 400
    deaths: { color: '#f87171', shadow: 'rgba(248, 113, 113, 0.4)' }, // Red 400
    loot: { color: '#22d3ee', shadow: 'rgba(34, 211, 238, 0.4)' }, // Cyan 400
    bosses: { color: '#fb923c', shadow: 'rgba(251, 146, 60, 0.4)' }, // Orange 400
    skill_shots: { color: '#60a5fa', shadow: 'rgba(96, 165, 250, 0.4)' }, // Blue 400
    milestones: { color: '#c084fc', shadow: 'rgba(192, 132, 252, 0.4)' }, // Purple 400
    glitches: { color: '#4ade80', shadow: 'rgba(74, 222, 128, 0.4)' }, // Green 400
    fails: { color: '#94a3b8', shadow: 'rgba(148, 163, 184, 0.4)' }, // Slate 400
    scares: { color: '#e2e8f0', shadow: 'rgba(226, 232, 240, 0.4)' }, // Slate 200
    streaks: { color: '#f472b6', shadow: 'rgba(244, 114, 182, 0.4)' }, // Pink 400
};

const DEFAULT_THEME = { color: '#a78bfa', shadow: 'rgba(167, 139, 250, 0.4)' };

// Simple client-side fallback to catch anything that might slip through
const isMetadataSafe = (text: string) => {
    // Check for common red flags or extremely long strings that might be spam
    if (text.length > 100) return false;
    // If it's all stars (server censored it), it's safe to show
    if (text === '****') return true;
    return true;
};

const StatItem: React.FC<{ id: string, stat: GameStat, index: number }> = ({ id, stat, index }) => {
    const [isIncrementing, setIsIncrementing] = useState(false);
    const prevValueRef = useRef(stat.value);
    const theme = THEMES[id] || DEFAULT_THEME;
    
    // Force re-mount when value changes to re-trigger animations
    const [valueKey, setValueKey] = useState(0);

    useEffect(() => {
        if (stat.value > prevValueRef.current) {
            // Increment key to force re-mount of animation elements
            setValueKey(k => k + 1);
            setIsIncrementing(true);
            const timer = setTimeout(() => setIsIncrementing(false), 400);
            prevValueRef.current = stat.value;
            return () => clearTimeout(timer);
        }
        prevValueRef.current = stat.value;
    }, [stat.value]);

    return (
        <div
            key={valueKey}
            className="relative flex flex-col items-center px-4 py-2 animate-pop-in opacity-0 will-change-transform w-32 min-h-[85px] flex-none"
            style={{ animationDelay: `${index * 80}ms` }}
        >
            {/* Subtle glow background */}
            <div
                className="absolute inset-x-4 top-4 bottom-8 opacity-10 blur-xl pointer-events-none rounded-full"
                style={{ backgroundColor: theme.color }}
            />

            {/* Separator Line */}
            {index > 0 && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[1px] h-8 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
            )}

            <div className="flex flex-col items-center gap-0.5 z-10">
                <span
                    className="text-[10px] font-black text-white/60 uppercase tracking-[0.25em] select-none whitespace-nowrap"
                    style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                >
                    {stat.label || id.replace(/_/g, ' ')}
                </span>
                <span
                    className={`text-[28px] font-black tabular-nums transition-all duration-200 ${isIncrementing ? 'animate-stat-increment' : ''}`}
                    style={{
                        color: theme.color,
                        textShadow: `
                            0 2px 4px rgba(0,0,0,0.8),
                            0 4px 12px ${theme.shadow},
                            0 0 20px ${theme.shadow}
                        `
                    }}
                >
                    {stat.value}
                </span>
            </div>

            {/* Metadata container with extra bottom padding and overflow-visible for shadows */}
            <div className="mt-1.5 h-6 flex items-start justify-center overflow-visible z-10">
                {stat.lastUpdateMetadata && isMetadataSafe(stat.lastUpdateMetadata) && (
                    <p
                        className="text-[10px] font-bold leading-none text-white/90 whitespace-nowrap px-2 pb-5"
                        style={{
                            textShadow: '0 2px 4px rgba(0,0,0,1)',
                            maskImage: 'linear-gradient(to right, black 85%, transparent 100%)',
                            maxWidth: '130px',
                            overflow: 'hidden'
                        }}
                    >
                        {stat.lastUpdateMetadata}
                    </p>
                )}
            </div>
        </div>
    );
};

export const GameStatsOverlay: React.FC<GameStatsOverlayProps> = ({ stats, containerClassName, scale }) => {
    // Render stats immediately from props
    const visibleStats = Object.entries(stats)
        .filter(([, stat]) => (stat.value || 0) > 0)
        .sort((a, b) => (b[1].updatedAt || 0) - (a[1].updatedAt || 0))
        .slice(0, 5); // Limit to top 5

    if (visibleStats.length === 0) return null;

    const resolvedScale = scale !== undefined ? scale : 1.0;

    return (
        <div className={containerClassName || "fixed inset-0 pointer-events-none flex items-center justify-center overflow-visible"}>
            <div
                className="flex items-center gap-0 px-4 flex-nowrap min-w-max"
                style={{
                    transform: `scale(${resolvedScale})`,
                    transformOrigin: 'center center'
                }}
            >
                {visibleStats.map(([id, stat], index) => (
                    <StatItem key={id} id={id} stat={stat} index={index} />
                ))}
            </div>
        </div>
    );
};
